import { supabase } from "@/integrations/supabase/client";

export type WishlistType = "wishlist" | "registry" | "personal";

export interface Wishlist {
  id: string;
  user_id: string;
  type: WishlistType;
  title: string;
  description: string | null;
  event_date: string | null;
  cover_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface WishItem {
  id: string;
  wishlist_id: string;
  title: string;
  description: string | null;
  price: number | null;
  quantity: number;
  image_url: string | null;
  link_url: string | null;
  category: string | null;
  is_most_desired: boolean;
  is_claimed: boolean;
  claimed_by_name: string | null;
  claimed_at: string | null;
  created_at: string;
}

export interface RecipientGiftProfile {
  display_name: string | null;
  email: string | null;
  preferences_wants: string | null;
  preferences_has: string | null;
  preferences_style: string | null;
  preferences_registries: string[] | null;
}

export async function getWishlistsByUser(userId: string) {
  const { data, error } = await supabase
    .from("wishlists" as any)
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  return { data: data as Wishlist[] | null, error };
}

export async function getGiftDataByRecipientEmail(email: string) {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) {
    return { data: null, error: null };
  }

  const { data: profile, error: profileError } = await supabase
    .from("profiles")
    .select("user_id, display_name, email, preferences_wants, preferences_has, preferences_style, preferences_registries")
    .ilike("email", normalizedEmail)
    .maybeSingle();

  if (profileError) {
    return { data: null, error: profileError };
  }

  if (!profile) {
    return {
      data: {
        profile: null,
        wishlists: [],
      },
      error: null,
    };
  }

  const { data: wishlists, error: wishlistsError } = await supabase
    .from("wishlists" as any)
    .select("*")
    .eq("user_id", (profile as { user_id?: string }).user_id)
    .order("created_at", { ascending: false });

  return {
    data: {
      profile: profile as RecipientGiftProfile,
      wishlists: (wishlists as Wishlist[] | null) ?? [],
    },
    error: wishlistsError,
  };
}

export async function getWishlistById(id: string) {
  const { data, error } = await supabase
    .from("wishlists" as any)
    .select("*")
    .eq("id", id)
    .maybeSingle();
  return { data: data as Wishlist | null, error };
}

export async function createWishlist(wishlist: {
  user_id: string;
  type: WishlistType;
  title: string;
  description?: string;
  event_date?: string;
  cover_url?: string;
}) {
  const { data, error } = await supabase
    .from("wishlists" as any)
    .insert(wishlist)
    .select()
    .single();
  return { data: data as Wishlist | null, error };
}

export async function updateWishlist(id: string, updates: Partial<Wishlist>) {
  const { data, error } = await supabase
    .from("wishlists" as any)
    .update(updates)
    .eq("id", id);
  return { data, error };
}

export async function deleteWishlist(id: string) {
  const { error } = await supabase
    .from("wishlists" as any)
    .delete()
    .eq("id", id);
  return { error };
}

export async function getWishItems(wishlistId: string) {
  const { data, error } = await supabase
    .from("wish_items" as any)
    .select("*")
    .eq("wishlist_id", wishlistId)
    .order("is_most_desired", { ascending: false })
    .order("created_at", { ascending: false });
  return { data: data as WishItem[] | null, error };
}

export async function createWishItem(item: {
  wishlist_id: string;
  title: string;
  description?: string;
  price?: number;
  quantity?: number;
  image_url?: string;
  link_url?: string;
  category?: string;
  is_most_desired?: boolean;
}) {
  const { data, error } = await supabase
    .from("wish_items" as any)
    .insert(item)
    .select()
    .single();
  return { data: data as WishItem | null, error };
}

export async function updateWishItem(id: string, updates: Partial<WishItem>) {
  const { data, error } = await supabase
    .from("wish_items" as any)
    .update(updates)
    .eq("id", id);
  return { data, error };
}

export async function deleteWishItem(id: string) {
  const { error } = await supabase
    .from("wish_items" as any)
    .delete()
    .eq("id", id);
  return { error };
}
